<template>
  <Teleport to=".app-shell">
    <div
      ref="barEl"
      class="replay-bar"
      :class="{ collapsed, placed: anchor !== null, dragging }"
      :style="barStyle"
      @pointerdown="onChromePointerDown"
    >
      <button
        type="button"
        class="replay-btn replay-fold"
        :aria-expanded="!collapsed"
        :aria-label="foldTitle"
        :title="foldTitle"
        @pointerdown.stop="onButtonPointerDown"
        @click="onFoldClick"
        @keydown="onFoldKey"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path v-if="collapsed" fill="currentColor" d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          <path v-else fill="currentColor" d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
      </button>
      <template v-if="!collapsed">
        <button
          type="button"
          class="replay-btn"
          :aria-label="playLabel"
          :title="playLabel"
          @pointerdown.stop="onButtonPointerDown"
          @click="onToggleClick"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path v-if="playing" fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            <path v-else fill="currentColor" d="M8 5v14l11-7z" />
          </svg>
        </button>
        <button
          type="button"
          class="replay-btn"
          :class="{ 'is-disabled': playing }"
          :aria-disabled="playing"
          :aria-label="t('duel.replay.step')"
          :title="t('duel.replay.step')"
          @pointerdown.stop="onButtonPointerDown"
          @click="onStepClick"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path fill="currentColor" d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
          </svg>
        </button>
        <button
          type="button"
          class="replay-btn replay-speed"
          :aria-label="t('duel.replay.speedLabel', { n: speed })"
          :title="t('duel.replay.speedLabel', { n: speed })"
          @pointerdown.stop="onButtonPointerDown"
          @click="onSpeedClick"
        >
          {{ t('duel.replay.speed', { n: speed }) }}
        </button>
      </template>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from '../i18n/t'

const props = defineProps<{
  playing: boolean
  speed: 1 | 2 | 4
}>()

const emit = defineEmits<{
  toggle: []
  step: []
  cycleSpeed: []
}>()

/** Top-left corner, so a size change (collapse, rotation) never shifts the fold button. */
const POS_KEY = 'dc-replay-bar-at'
const FOLD_KEY = 'dc-replay-bar-fold'
const EDGE = 8
const OPEN_SIZE = { w: 184, h: 48 }
const FOLDED_SIZE = { w: 48, h: 48 }

type Anchor = { x: number; y: number; rx: number; ry: number }

const { t } = useI18n()
const barEl = ref<HTMLElement | null>(null)
const collapsed = ref(readFold())
const barSize = ref(collapsed.value ? FOLDED_SIZE : OPEN_SIZE)
const anchor = ref<Anchor | null>(readAnchor())
const dragging = ref(false)
let suppressClick = false
let dragLive = false

const foldTitle = computed(() => {
  const action = collapsed.value ? t('duel.replay.expand') : t('duel.replay.collapse')
  return `${action}. ${t('duel.replay.move')}`
})

const playLabel = computed(() =>
  props.playing ? t('duel.replay.pause') : t('duel.replay.play'),
)

const barStyle = computed(() => {
  const current = anchor.value
  if (!current) return undefined
  return {
    left: `${current.x}px`,
    top: `${current.y}px`,
    right: 'auto',
    bottom: 'auto',
    transform: 'none',
  }
})

function readFold(): boolean {
  try {
    return localStorage.getItem(FOLD_KEY) === '1'
  } catch {
    return false
  }
}

function persistFold(): void {
  try {
    localStorage.setItem(FOLD_KEY, collapsed.value ? '1' : '0')
  } catch {
    /* private mode or a full quota */
  }
}

function readAnchor(): Anchor | null {
  try {
    const raw = localStorage.getItem(POS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { rx?: unknown; ry?: unknown }
    const rx = Number(parsed.rx)
    const ry = Number(parsed.ry)
    if (!Number.isFinite(rx) || !Number.isFinite(ry)) return null
    return anchorFromRatio(Math.min(1, Math.max(0, rx)), Math.min(1, Math.max(0, ry)))
  } catch {
    return null
  }
}

function persistAnchor(): void {
  try {
    if (!anchor.value) localStorage.removeItem(POS_KEY)
    else localStorage.setItem(POS_KEY, JSON.stringify(anchor.value))
  } catch {
    /* private mode or a full quota */
  }
}

function freeSpan(): { sx: number; sy: number } {
  const { w, h } = barSize.value
  return {
    sx: Math.max(1, window.innerWidth - w - EDGE * 2),
    sy: Math.max(1, window.innerHeight - h - EDGE * 2),
  }
}

function anchorFromPixels(pos: { x: number; y: number }): Anchor {
  const { sx, sy } = freeSpan()
  const x = Math.round(Math.min(EDGE + sx, Math.max(EDGE, pos.x)))
  const y = Math.round(Math.min(EDGE + sy, Math.max(EDGE, pos.y)))
  return { x, y, rx: (x - EDGE) / sx, ry: (y - EDGE) / sy }
}

function anchorFromRatio(rx: number, ry: number): Anchor {
  const { sx, sy } = freeSpan()
  return anchorFromPixels({ x: EDGE + rx * sx, y: EDGE + ry * sy })
}

function measure(): void {
  const el = barEl.value
  if (!el) return
  const w = el.offsetWidth
  const h = el.offsetHeight
  if (w <= 0 || h <= 0) return
  if (w === barSize.value.w && h === barSize.value.h) return
  barSize.value = { w, h }
}

function currentCorner(): { x: number; y: number } {
  if (anchor.value) return anchor.value
  const rect = barEl.value?.getBoundingClientRect()
  return rect ? { x: rect.left, y: rect.top } : { x: EDGE, y: EDGE }
}

/** Keeps the same corner, pulled back on screen if the new size would overflow. */
function refit(): void {
  measure()
  const current = anchor.value
  if (!current) return
  const next = anchorFromPixels(current)
  if (next.x === current.x && next.y === current.y) return
  anchor.value = next
  persistAnchor()
}

function onResize(): void {
  measure()
  const current = anchor.value
  if (current) anchor.value = anchorFromRatio(current.rx, current.ry)
}

onMounted(async () => {
  await nextTick()
  measure()
  const current = anchor.value
  if (current) anchor.value = anchorFromRatio(current.rx, current.ry)
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})

watch(collapsed, async () => {
  await nextTick()
  refit()
})

function startDrag(event: PointerEvent, fromButton: boolean): void {
  if (event.button !== 0 || dragLive) return
  const handle = event.currentTarget
  if (!(handle instanceof HTMLElement)) return
  try {
    handle.setPointerCapture(event.pointerId)
  } catch {
    return
  }
  dragLive = true
  const originX = event.clientX
  const originY = event.clientY
  let start: { x: number; y: number } | null = null
  let moved = false
  const prevSelect = document.body.style.userSelect

  const onMove = (ev: PointerEvent) => {
    const dx = ev.clientX - originX
    const dy = ev.clientY - originY
    if (!moved && Math.hypot(dx, dy) < 8) return
    if (!moved) {
      moved = true
      dragging.value = true
      document.body.style.userSelect = 'none'
      measure()
      start = currentCorner()
    }
    if (start) anchor.value = anchorFromPixels({ x: start.x + dx, y: start.y + dy })
  }
  const onUp = (ev: PointerEvent) => {
    handle.removeEventListener('pointermove', onMove)
    handle.removeEventListener('pointerup', onUp)
    handle.removeEventListener('pointercancel', onUp)
    if (handle.hasPointerCapture(ev.pointerId)) handle.releasePointerCapture(ev.pointerId)
    dragLive = false
    dragging.value = false
    document.body.style.userSelect = prevSelect
    if (!moved) return
    // A cancel does not emit click. A finished drag does, and that click must not toggle.
    if (fromButton && ev.type !== 'pointercancel') suppressClick = true
    persistAnchor()
  }
  handle.addEventListener('pointermove', onMove)
  handle.addEventListener('pointerup', onUp)
  handle.addEventListener('pointercancel', onUp)
}

function onChromePointerDown(event: PointerEvent): void {
  if (event.target !== event.currentTarget) return
  startDrag(event, false)
}

function onButtonPointerDown(event: PointerEvent): void {
  startDrag(event, true)
}

function consumeClick(): boolean {
  if (!suppressClick) return false
  suppressClick = false
  return true
}

function onFoldClick(): void {
  if (consumeClick()) return
  collapsed.value = !collapsed.value
  persistFold()
}

function onToggleClick(): void {
  if (consumeClick()) return
  emit('toggle')
}

function onStepClick(): void {
  if (consumeClick()) return
  if (props.playing) return
  emit('step')
}

function onSpeedClick(): void {
  if (consumeClick()) return
  emit('cycleSpeed')
}

function nudge(dx: number, dy: number): void {
  measure()
  const start = currentCorner()
  anchor.value = anchorFromPixels({ x: start.x + dx, y: start.y + dy })
  persistAnchor()
}

function onFoldKey(event: KeyboardEvent): void {
  const step = event.shiftKey ? 48 : 16
  if (event.key === 'ArrowLeft') nudge(-step, 0)
  else if (event.key === 'ArrowRight') nudge(step, 0)
  else if (event.key === 'ArrowUp') nudge(0, -step)
  else if (event.key === 'ArrowDown') nudge(0, step)
  else if (event.key === 'Home') {
    anchor.value = null
    persistAnchor()
  } else return
  event.preventDefault()
  event.stopPropagation()
}
</script>

<style scoped>
.replay-bar {
  position: fixed;
  /* Above the duel view, its overlays and the global chat. */
  z-index: 10050;
  left: 50%;
  bottom: max(16px, env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 4px;
  width: max-content;
  padding: 4px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-2);
  touch-action: none;
  user-select: none;
  transition: opacity var(--md-motion-short) var(--md-motion-standard);
}

.replay-bar.placed {
  transform: none;
}

.replay-bar.collapsed {
  opacity: 0.55;
}

.replay-bar.collapsed:hover,
.replay-bar.collapsed:focus-within,
.replay-bar.collapsed.dragging {
  opacity: 1;
}

.replay-btn {
  width: 40px;
  height: 40px;
  padding: 0;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid rgba(232, 197, 71, 0.32);
  background: rgba(12, 14, 24, 0.9);
  color: #f4f0ea;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
}

.replay-fold {
  cursor: grab;
}

.replay-btn.is-disabled {
  opacity: 0.5;
}

.replay-btn:hover:not(.is-disabled) {
  border-color: #e8c547;
  color: #fde68a;
}

.replay-btn:focus-visible {
  outline: 2px solid #e8c547;
  outline-offset: 2px;
}

.replay-bar.dragging .replay-btn {
  cursor: grabbing;
}

@media (prefers-reduced-motion: reduce) {
  .replay-bar {
    transition: none;
  }
}
</style>
